<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import {
  useStore,
  useStoreGetters,
  useMapGetter,
} from 'dashboard/composables/store';
import { useAutomation } from 'dashboard/composables/useAutomation';
import { useEditableAutomation } from 'dashboard/composables/useEditableAutomation';
import AutomationRuleForm from './AutomationRuleForm.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import FormInput from 'v3/components/Form/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import BackButton from 'dashboard/components/widgets/BackButton.vue';
import { AUTOMATION_ACTION_TYPES_ALL } from './constants';

const START_VALUE = {
  name: null,
  description: null,
  active: false,
  event_name: 'conversation_opened',
  conditions: [
    {
      attribute_key: 'status',
      filter_operator: 'equal_to',
      values: '',
      query_operator: 'and',
      custom_attribute_type: '',
    },
  ],
  actions: [
    {
      action_name: 'assign_agent',
      action_params: [],
    },
  ],
};

const route = useRoute();
const router = useRouter();
const store = useStore();
const { t } = useI18n();
const getters = useStoreGetters();
const allCustomAttributes = useMapGetter('attributes/getAttributes');

const {
  automation,
  automationTypes,
  onEventChange,
  getConditionDropdownValues,
  appendNewCondition,
  appendNewAction,
  insertActionAfter,
  removeFilter,
  removeAction,
  resetAction,
  getActionDropdownValues,
  manifestCustomAttributes,
} = useAutomation(structuredClone(START_VALUE));

const { formatAutomation } = useEditableAutomation();

const accountId = computed(() => getters.getCurrentAccountId.value);
const records = computed(() => getters['automations/getAutomations'].value);
const uiFlags = computed(() => getters['automations/getUIFlags'].value);

const isSLAEnabled = computed(() =>
  getters['accounts/isFeatureEnabledonAccount'].value(accountId.value, 'sla')
);

const isCreate = computed(() => route.name === 'automation_editor_new');

const editorInstanceKey = computed(() =>
  isCreate.value ? 'new' : String(route.params.automationId ?? '')
);

const listRoute = computed(() => ({
  name: 'automation_list',
  params: { accountId: route.params.accountId },
}));

const syncFormStateFromRoute = () => {
  manifestCustomAttributes();
  if (isCreate.value) {
    automation.value = structuredClone(START_VALUE);
    return;
  }
  const id = Number(route.params.automationId);
  const record = records.value.find(r => r.id === id);
  if (record?.deprecated_trigger) {
    useAlert(t('AUTOMATION.DEPRECATED_WORKFLOW'));
    router.replace(listRoute.value);
    return;
  }
  if (record?.conditions) {
    automation.value = formatAutomation(
      record,
      allCustomAttributes.value,
      automationTypes,
      AUTOMATION_ACTION_TYPES_ALL
    );
  }
};

watch(
  () => [route.name, route.params.automationId, records.value],
  () => {
    syncFormStateFromRoute();
  },
  { immediate: true }
);

onMounted(async () => {
  await store.dispatch('inboxes/get');
  await store.dispatch('agents/get');
  await store.dispatch('contacts/get');
  await store.dispatch('teams/get');
  await store.dispatch('labels/get');
  await store.dispatch('campaigns/get');
  await store.dispatch('automations/get');
  if (isSLAEnabled.value) {
    await store.dispatch('sla/get');
  }
  syncFormStateFromRoute();
});

const isReady = computed(() => {
  if (isCreate.value) return Boolean(automation.value?.conditions);
  const id = Number(route.params.automationId);
  if (Number.isNaN(id)) return false;
  const record = records.value.find(r => r.id === id);
  if (!record) return false;
  return Number(automation.value?.id) === id;
});

const notFound = computed(() => {
  if (isCreate.value) return false;
  if (uiFlags.value.isFetching) return false;
  const id = Number(route.params.automationId);
  if (Number.isNaN(id)) return true;
  return !records.value.some(r => r.id === id);
});

const isSaving = computed(
  () => uiFlags.value.isCreating || uiFlags.value.isUpdating
);

const goBack = () => {
  router.push(listRoute.value);
};

const submitAutomation = async (payload, mode, meta = {}) => {
  const { stayOnPage = false, forceActive } = meta;
  const body = { ...payload };
  if (typeof forceActive === 'boolean') {
    body.active = forceActive;
  }

  try {
    const successMessage = (() => {
      if (stayOnPage) return t('AUTOMATION.EDITOR.SAVE_SUCCESS');
      if (mode === 'edit') return t('AUTOMATION.EDIT.API.SUCCESS_MESSAGE');
      return t('AUTOMATION.ADD.API.SUCCESS_MESSAGE');
    })();

    let result;
    if (mode === 'edit') {
      result = await store.dispatch('automations/update', {
        id: body.id,
        ...body,
      });
    } else {
      result = await store.dispatch('automations/create', body);
    }

    useAlert(successMessage);

    if (stayOnPage) {
      if (mode === 'create' && result?.id) {
        await router.replace({
          name: 'automation_editor_edit',
          params: {
            accountId: route.params.accountId,
            automationId: String(result.id),
          },
        });
      } else if (
        mode === 'edit' &&
        result &&
        typeof result.active === 'boolean'
      ) {
        automation.value.active = result.active;
      }
    } else {
      await router.push(listRoute.value);
    }
  } catch (error) {
    const errorMessage =
      mode === 'edit'
        ? t('AUTOMATION.EDIT.API.ERROR_MESSAGE')
        : t('AUTOMATION.ADD.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};

const automationFormRef = ref(null);

const saveFromTopbar = () => {
  automationFormRef.value?.requestSave?.({ stayOnPage: true });
};

const publishFromTopbar = () => {
  automationFormRef.value?.requestSave?.({
    stayOnPage: false,
    forceActive: true,
  });
};

const metaDialogRef = ref(null);
const draftName = ref('');
const draftDescription = ref('');
const modalNameError = ref('');
const modalDescError = ref('');

const displayTitle = computed(() => {
  const name = automation.value?.name;
  if (name && String(name).trim()) return String(name).trim();
  return t('AUTOMATION.FLOW.DETAILS_PLACEHOLDER');
});

const displayDescription = computed(() => {
  const desc = automation.value?.description;
  if (desc && String(desc).trim()) return String(desc).trim();
  return t('AUTOMATION.EDITOR.DESCRIPTION_PLACEHOLDER');
});

const openMetaModal = () => {
  draftName.value = automation.value?.name || '';
  draftDescription.value = automation.value?.description || '';
  modalNameError.value = '';
  modalDescError.value = '';
  metaDialogRef.value?.open();
};

const onMetaDialogClose = () => {
  modalNameError.value = '';
  modalDescError.value = '';
};

const applyMetaFromModal = () => {
  modalNameError.value = '';
  modalDescError.value = '';
  const name = String(draftName.value || '').trim();
  const desc = String(draftDescription.value || '').trim();
  if (!name) {
    modalNameError.value = t('AUTOMATION.ADD.FORM.NAME.ERROR');
  }
  if (!desc) {
    modalDescError.value = t('AUTOMATION.ADD.FORM.DESC.ERROR');
  }
  if (modalNameError.value || modalDescError.value) return;
  automation.value.name = name;
  automation.value.description = desc;
  automationFormRef.value?.resetValidation?.();
  metaDialogRef.value?.close();
};

const onValidationFailed = errs => {
  if (!errs || (!errs.name && !errs.description)) return;
  draftName.value = automation.value?.name || '';
  draftDescription.value = automation.value?.description || '';
  modalNameError.value = errs.name ? t('AUTOMATION.ADD.FORM.NAME.ERROR') : '';
  modalDescError.value = errs.description
    ? t('AUTOMATION.ADD.FORM.DESC.ERROR')
    : '';
  metaDialogRef.value?.open();
};
</script>

<template>
  <div
    class="flex h-full min-h-0 w-full min-w-0 flex-1 flex-col overflow-hidden"
  >
    <woot-loading-state
      v-if="!isCreate && uiFlags.isFetching && !records.length"
      :message="$t('AUTOMATION.LOADING')"
    />
    <p
      v-else-if="notFound"
      class="py-16 text-center text-body-main text-n-slate-11"
    >
      {{ $t('AUTOMATION.EDITOR.NOT_FOUND') }}
    </p>
    <template v-else-if="isReady && automation">
      <div
        class="mb-0 flex h-12 w-full shrink-0 items-center justify-between border-b border-n-weak bg-n-solid-2 px-4 dark:border-n-strong"
      >
        <div class="flex w-1/2 min-w-0 items-center gap-2">
          <BackButton :back-url="listRoute" icon-only class="shrink-0" />
          <div class="flex min-w-0 shrink items-center gap-1">
            <div class="flex min-w-0 flex-col">
              <h1
                class="m-0 truncate text-sm font-semibold leading-tight text-n-slate-12"
              >
                {{ displayTitle }}
              </h1>
              <p class="m-0 truncate text-xs leading-tight text-n-slate-11">
                {{ displayDescription }}
              </p>
            </div>
            <NextButton
              icon="i-lucide-pencil"
              faded
              slate
              sm
              class="shrink-0"
              :aria-label="$t('AUTOMATION.EDITOR.TOPBAR_EDIT')"
              :title="$t('AUTOMATION.EDITOR.TOPBAR_EDIT')"
              @click="openMetaModal"
            />
          </div>
        </div>
        <div class="flex w-1/2 shrink-0 items-center justify-end gap-2">
          <NextButton
            faded
            slate
            sm
            :label="$t('AUTOMATION.EDITOR.SAVE')"
            :is-loading="isSaving"
            :disabled="isSaving"
            @click="saveFromTopbar"
          />
          <NextButton
            solid
            blue
            sm
            :label="$t('AUTOMATION.EDITOR.PUBLISH')"
            :is-loading="isSaving"
            :disabled="isSaving"
            @click="publishFromTopbar"
          />
        </div>
      </div>
      <div class="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        <AutomationRuleForm
          :key="editorInstanceKey"
          ref="automationFormRef"
          v-model:automation="automation"
          :mode="isCreate ? 'create' : 'edit'"
          :automation-types="automationTypes"
          :get-condition-dropdown-values="getConditionDropdownValues"
          :get-action-dropdown-values="getActionDropdownValues"
          :append-new-condition="appendNewCondition"
          :append-new-action="appendNewAction"
          :insert-action-after="insertActionAfter"
          :remove-filter="removeFilter"
          :remove-action="removeAction"
          :reset-action="resetAction"
          :on-event-change="onEventChange"
          @save="submitAutomation"
          @cancel="goBack"
          @validation-failed="onValidationFailed"
        />
      </div>
    </template>

    <Dialog
      ref="metaDialogRef"
      type="edit"
      :title="t('AUTOMATION.EDITOR.META_MODAL_TITLE')"
      width="lg"
      :confirm-button-label="t('AUTOMATION.EDITOR.META_SAVE')"
      :cancel-button-label="t('AUTOMATION.FORM.CANCEL')"
      @confirm="applyMetaFromModal"
      @close="onMetaDialogClose"
    >
      <div class="flex w-full flex-col gap-4">
        <div class="flex flex-col gap-1">
          <FormInput
            v-model="draftName"
            name="automation-meta-name"
            spacing="compact"
            :label="$t('AUTOMATION.ADD.FORM.NAME.LABEL')"
            :placeholder="$t('AUTOMATION.ADD.FORM.NAME.PLACEHOLDER')"
            :has-error="Boolean(modalNameError)"
            :error-message="modalNameError"
          />
        </div>
        <TextArea
          id="automation-meta-description"
          v-model="draftDescription"
          :label="$t('AUTOMATION.ADD.FORM.DESC.LABEL')"
          :placeholder="$t('AUTOMATION.ADD.FORM.DESC.PLACEHOLDER')"
          :message="modalDescError"
          :message-type="modalDescError ? 'error' : 'info'"
          resize
          min-height="5rem"
        />
      </div>
    </Dialog>
  </div>
</template>
